import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Mail,
  Edit,
  Send,
  RotateCcw,
  History,
  X,
  AlertCircle,
  Eye,
  Code,
  Check,
  Info,
} from 'lucide-react';
import { auth, db, functions } from '../../firebase';
import { EcointelLottieMark } from '../../components/EcoIntelLoader';
import { useUserFacingError } from '../../hooks/useUserFacingError';
import AdminTable from '../../components/admin/AdminTable';
import {
  EMAIL_TEMPLATES_COLLECTION,
  EMAIL_TEMPLATE_VERSIONS_SUBCOLLECTION,
  EMAIL_TEMPLATE_LIST,
  EMAIL_AUDIENCE_LABELS,
  EMAIL_CATEGORY_LABELS,
  resolveEmailTemplate,
  sanitizeEmailHtml,
} from '../../constants/emailTemplates';
import { renderTemplatePair } from '../../utils/emailTemplateRender';

const STATE_PILL = {
  default: { label: 'Default', className: 'bg-slate-100 text-slate-500 border-slate-200' },
  custom: { label: 'Customised', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  paused: { label: 'Paused (default live)', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

function StatePill({ state }) {
  const s = STATE_PILL[state] || STATE_PILL.default;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${s.className}`}>
      {s.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// History modal — last saved versions (versions sub-collection)
// ---------------------------------------------------------------------------
function HistoryModal({ template, onClose, onRestore }) {
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState(null);
  const [previewIdx, setPreviewIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          collection(db, EMAIL_TEMPLATES_COLLECTION, template.id, EMAIL_TEMPLATE_VERSIONS_SUBCOLLECTION),
        );
        const out = snap.docs
          .map((d) => ({ versionId: d.id, ...d.data() }))
          .sort((a, b) => (Number(b.version) || 0) - (Number(a.version) || 0))
          .slice(0, 10);
        if (!cancelled) setVersions(out);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load history.');
      }
    })();
    return () => { cancelled = true; };
  }, [template.id]);

  const preview = previewIdx != null && versions ? versions[previewIdx] : null;
  const previewHtml = preview
    ? renderTemplatePair({ subject: preview.subject, html: preview.html }, template.sampleData).html
    : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg flex items-center gap-2 text-slate-800">
            <History size={18} className="text-slate-500" /> Version history · {template.displayName}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {error ? (
            <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-sm">
              <AlertCircle size={16} className="shrink-0 mt-0.5" /><div>{error}</div>
            </div>
          ) : versions == null ? (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm gap-2">
              <EcointelLottieMark size={18} /> Loading history…
            </div>
          ) : versions.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No saved versions yet. Versions are recorded each time you save.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                {versions.map((v, i) => (
                  <div key={v.versionId} className={`border rounded-lg p-3 ${previewIdx === i ? 'border-[#043132] bg-slate-50' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">Version {v.version}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setPreviewIdx(i)} className="text-[11px] text-[#043132] hover:underline inline-flex items-center gap-1"><Eye size={11} /> Preview</button>
                        <button onClick={() => onRestore(v)} className="text-[11px] text-[#043132] hover:underline inline-flex items-center gap-1"><RotateCcw size={11} /> Restore</button>
                      </div>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 truncate">{v.subject || <span className="italic">no subject</span>}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {v.updatedAt?.toDate ? v.updatedAt.toDate().toLocaleString() : ''}
                      {v.updatedByEmail ? ` · ${v.updatedByEmail}` : ''}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white min-h-[200px]">
                {preview ? (
                  <iframe title="version-preview" sandbox="allow-same-origin" srcDoc={previewHtml} className="w-full h-full min-h-[300px]" />
                ) : (
                  <div className="p-6 text-center text-sm text-slate-400">Select a version to preview.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor modal — three panes: metadata + variables / editor / live preview
// ---------------------------------------------------------------------------
function EmailTemplateEditor({ template, onClose, onSaved }) {
  const { formatError, logError } = useUserFacingError();
  const [draft, setDraft] = useState({
    enabled: template.state === 'custom' ? true : (template.storedExists ? template.enabled : true),
    subject: template.subject,
    html: template.html,
  });
  const [sampleText, setSampleText] = useState(() => JSON.stringify(template.sampleData || {}, null, 2));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testStatus, setTestStatus] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [focusedField, setFocusedField] = useState('html');
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);

  const sampleData = useMemo(() => {
    try { return JSON.parse(sampleText); } catch { return null; }
  }, [sampleText]);

  const rendered = useMemo(() => {
    const ctx = sampleData || {};
    return renderTemplatePair({ subject: draft.subject, html: draft.html }, ctx);
  }, [draft.subject, draft.html, sampleData]);

  const insertToken = (path, raw) => {
    const token = raw ? `{{{ ${path} }}}` : `{{ ${path} }}`;
    if (focusedField === 'subject') {
      const el = subjectRef.current;
      const start = el ? el.selectionStart : draft.subject.length;
      const end = el ? el.selectionEnd : draft.subject.length;
      const next = draft.subject.slice(0, start) + token + draft.subject.slice(end);
      setDraft((d) => ({ ...d, subject: next }));
    } else {
      const el = htmlRef.current;
      const start = el ? el.selectionStart : draft.html.length;
      const end = el ? el.selectionEnd : draft.html.length;
      const next = draft.html.slice(0, start) + token + draft.html.slice(end);
      setDraft((d) => ({ ...d, html: next }));
    }
  };

  const handleSave = async () => {
    if (!draft.subject.trim()) { setError('Subject is required.'); return; }
    if (!draft.html.trim()) { setError('Email body is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      const cleanHtml = sanitizeEmailHtml(draft.html);
      const nextVersion = (Number(template.version) || 0) + 1;
      const payload = {
        id: template.id,
        audience: template.audience,
        category: template.category,
        displayName: template.displayName,
        description: template.description,
        enabled: !!draft.enabled,
        subject: draft.subject.trim(),
        html: cleanHtml,
        version: nextVersion,
        updatedAt: serverTimestamp(),
        updatedByEmail: auth.currentUser?.email || null,
        updatedByUid: auth.currentUser?.uid || null,
      };
      // Snapshot the saved version for rollback (history = the audit log).
      await setDoc(
        doc(db, EMAIL_TEMPLATES_COLLECTION, template.id, EMAIL_TEMPLATE_VERSIONS_SUBCOLLECTION, String(nextVersion)),
        {
          version: nextVersion,
          subject: payload.subject,
          html: payload.html,
          enabled: payload.enabled,
          updatedAt: serverTimestamp(),
          updatedByEmail: payload.updatedByEmail,
        },
      );
      await setDoc(doc(db, EMAIL_TEMPLATES_COLLECTION, template.id), payload, { merge: true });
      onSaved();
    } catch (e) {
      logError('Save email template failed', e);
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setTestStatus({ kind: 'pending', message: 'Sending test…' });
    try {
      const callable = httpsCallable(functions, 'sendTestEmail');
      const res = await callable({
        templateId: template.id,
        draft: { subject: draft.subject, html: sanitizeEmailHtml(draft.html) },
        sampleDataOverrides: sampleData || {},
      });
      const to = res?.data?.to;
      setTestStatus({ kind: 'ok', message: `Sent to ${to || 'your inbox'}.` });
    } catch (e) {
      logError('Send test email failed', e);
      setTestStatus({ kind: 'error', message: e?.message || 'Test send failed.' });
    }
  };

  const handleReset = () => {
    setDraft((d) => ({ ...d, subject: template.defaultSubject, html: template.defaultHtml, enabled: true }));
    setError(null);
  };

  const handleRestore = (version) => {
    setDraft((d) => ({
      ...d,
      subject: version.subject || template.defaultSubject,
      html: version.html || template.defaultHtml,
      enabled: version.enabled !== false,
    }));
    setShowHistory(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl my-8 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2 text-slate-800">
              <Mail size={18} className="text-slate-500" /> {template.displayName}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{template.description}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        {error ? (
          <div className="mx-5 mt-4 flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-sm">
            <AlertCircle size={16} className="shrink-0 mt-0.5" /><div>{error}</div>
          </div>
        ) : null}

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[260px_1fr_1fr] gap-0 min-h-0">
          {/* Left: metadata + variable cheat-sheet */}
          <div className="border-r border-slate-100 p-4 overflow-y-auto space-y-4">
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{EMAIL_AUDIENCE_LABELS[template.audience] || template.audience}</span>
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{EMAIL_CATEGORY_LABELS[template.category] || template.category}</span>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                className="rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
              />
              <span className="font-medium text-slate-700">Use custom content</span>
            </label>
            <p className="text-[10px] text-slate-400 -mt-2">When unchecked, the built-in default is sent (your edits are kept but paused).</p>

            <div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Available variables</div>
              <div className="space-y-1">
                {(template.variableSchema || []).map((v) => (
                  <button
                    key={v.path}
                    type="button"
                    onClick={() => insertToken(v.path, v.raw)}
                    title={`${v.description || ''}${v.raw ? ' (raw HTML)' : ''}\nInserts into the ${focusedField} field`}
                    className="w-full text-left text-[11px] px-2 py-1 rounded border border-slate-200 hover:border-[#043132] hover:bg-slate-50 font-mono text-slate-700"
                  >
                    {v.raw ? `{{{ ${v.path} }}}` : `{{ ${v.path} }}`}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2 flex items-start gap-1">
                <Info size={11} className="shrink-0 mt-0.5" />
                Variables not in this list won’t substitute. Contact engineering to add new data fields to a trigger.
              </p>
            </div>
          </div>

          {/* Centre: editor */}
          <div className="border-r border-slate-100 p-4 overflow-y-auto space-y-4 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Subject</label>
              <textarea
                ref={subjectRef}
                value={draft.subject}
                onFocus={() => setFocusedField('subject')}
                onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value.replace(/\n/g, '').slice(0, 200) }))}
                rows={2}
                className="w-full border border-slate-200 rounded-lg p-2 text-sm font-mono focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                placeholder="Subject line (productName prefix is added automatically)"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Code size={12} /> HTML body
              </label>
              <textarea
                ref={htmlRef}
                value={draft.html}
                onFocus={() => setFocusedField('html')}
                onChange={(e) => setDraft((d) => ({ ...d, html: e.target.value }))}
                rows={22}
                spellCheck={false}
                className="w-full border border-slate-200 rounded-lg p-2 text-[12px] font-mono leading-relaxed focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              />
            </div>
          </div>

          {/* Right: live preview + sample data */}
          <div className="p-4 overflow-y-auto space-y-3 min-w-0">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                <Eye size={12} /> Live preview
              </label>
              <div className="text-[11px] text-slate-500 mb-1 truncate">
                <span className="font-semibold">Subject:</span> {rendered.subject || <span className="italic text-slate-400">empty</span>}
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <iframe title="email-preview" sandbox="allow-same-origin" srcDoc={rendered.html} className="w-full h-[360px]" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Sample data (preview &amp; test)</label>
              <textarea
                value={sampleText}
                onChange={(e) => setSampleText(e.target.value)}
                rows={6}
                spellCheck={false}
                className={`w-full border rounded-lg p-2 text-[11px] font-mono outline-none ${sampleData == null ? 'border-rose-300 focus:border-rose-400' : 'border-slate-200 focus:border-[#043132] focus:ring-1 focus:ring-[#043132]'}`}
              />
              {sampleData == null ? <p className="text-[10px] text-rose-500 mt-0.5">Invalid JSON. Preview is paused.</p> : null}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowHistory(true)} className="px-3 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium inline-flex items-center gap-1.5"><History size={15} /> History</button>
            <button type="button" onClick={handleReset} className="px-3 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium inline-flex items-center gap-1.5"><RotateCcw size={15} /> Reset to default</button>
          </div>
          <div className="flex items-center gap-2">
            {testStatus ? (
              <span className={`text-[11px] inline-flex items-center gap-1 ${testStatus.kind === 'error' ? 'text-rose-600' : testStatus.kind === 'ok' ? 'text-emerald-600' : 'text-slate-500'}`}>
                {testStatus.kind === 'ok' ? <Check size={12} /> : testStatus.kind === 'pending' ? <EcointelLottieMark size={12} /> : <AlertCircle size={12} />}
                {testStatus.message}
              </span>
            ) : null}
            <button type="button" onClick={handleSendTest} className="px-3 py-2 border border-slate-200 rounded-lg text-slate-700 hover:border-slate-300 font-medium text-sm inline-flex items-center gap-1.5"><Send size={15} /> Send test to me</button>
            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#043132] text-white rounded-lg hover:bg-[#065354] font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2">
              {saving ? <><EcointelLottieMark size={14} /> Saving…</> : 'Save template'}
            </button>
          </div>
        </div>
      </div>

      {showHistory ? (
        <HistoryModal template={template} onClose={() => setShowHistory(false)} onRestore={handleRestore} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function EmailTemplatesAdmin() {
  const [storedById, setStoredById] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [audienceFilter, setAudienceFilter] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, EMAIL_TEMPLATES_COLLECTION),
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => { map[d.id] = d.data(); });
        setStoredById(map);
        setLoading(false);
      },
      (err) => {
        console.warn('[EmailTemplatesAdmin] listener error:', err?.message || err);
        setStoredById({});
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const rows = useMemo(
    () => EMAIL_TEMPLATE_LIST.map((def) => resolveEmailTemplate(def.id, storedById[def.id])).filter(Boolean),
    [storedById],
  );

  const filteredRows = useMemo(() => rows.filter((r) => {
    if (audienceFilter.length && !audienceFilter.includes(r.audience)) return false;
    if (categoryFilter.length && !categoryFilter.includes(r.category)) return false;
    if (stateFilter.length && !stateFilter.includes(r.state)) return false;
    return true;
  }), [rows, audienceFilter, categoryFilter, stateFilter]);

  const categoryOptions = useMemo(() => {
    const seen = [];
    EMAIL_TEMPLATE_LIST.forEach((t) => { if (!seen.includes(t.category)) seen.push(t.category); });
    return seen.map((c) => ({ value: c, label: EMAIL_CATEGORY_LABELS[c] || c }));
  }, []);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
            <Mail size={22} /> Email templates
          </h1>
          <p className="text-xs text-slate-500 mt-0.5 max-w-3xl">
            Subjects and bodies for the transactional emails the platform sends via Resend. Edits take effect
            without a redeploy. Templates with no customisation send the built-in default.
          </p>
        </div>
      </div>

      <AdminTable
        rows={filteredRows}
        loading={loading}
        rowKey={(r) => r.id}
        searchPlaceholder="Search templates by name…"
        searchPredicate={(r, q) => (
          (r.displayName || '').toLowerCase().includes(q) ||
          (r.description || '').toLowerCase().includes(q) ||
          (r.id || '').toLowerCase().includes(q)
        )}
        filterGroups={[
          {
            id: 'state',
            label: 'State',
            selected: stateFilter,
            onChange: setStateFilter,
            options: [
              { value: 'default', label: 'Default' },
              { value: 'custom', label: 'Customised' },
              { value: 'paused', label: 'Paused' },
            ],
          },
          {
            id: 'audience',
            label: 'Audience',
            selected: audienceFilter,
            onChange: setAudienceFilter,
            options: [
              { value: 'customer', label: 'Customer' },
              { value: 'admin', label: 'Admin' },
            ],
          },
          {
            id: 'category',
            label: 'Category',
            selected: categoryFilter,
            onChange: setCategoryFilter,
            options: categoryOptions,
          },
        ]}
        defaultPageSize={25}
        emptyMessage="No email templates."
        columns={[
          { key: 'state', label: 'State', sortable: false },
          { key: 'displayName', label: 'Template', sortValue: (r) => (r.displayName || '').toLowerCase() },
          { key: 'audience', label: 'Audience', sortValue: (r) => r.audience },
          { key: 'category', label: 'Category', sortValue: (r) => r.category },
          { key: 'version', label: 'Version', sortValue: (r) => r.version, align: 'right', className: 'text-right' },
          { key: 'updated', label: 'Updated', sortValue: (r) => (r.updatedAt ? Date.parse(r.updatedAt) || 0 : 0) },
          { key: 'actions', label: 'Actions', align: 'right', sortable: false, className: 'text-right' },
        ]}
        renderRow={(r) => (
          <>
            <td className="p-4"><StatePill state={r.state} /></td>
            <td className="p-4">
              <div className="text-sm font-medium text-slate-800">{r.displayName}</div>
              <div className="text-[11px] text-slate-400 line-clamp-1 max-w-md">{r.description}</div>
            </td>
            <td className="p-4 text-sm text-slate-700">{EMAIL_AUDIENCE_LABELS[r.audience] || r.audience}</td>
            <td className="p-4 text-sm text-slate-700">{EMAIL_CATEGORY_LABELS[r.category] || r.category}</td>
            <td className="p-4 text-right text-sm tabular-nums text-slate-700">{r.version || '—'}</td>
            <td className="p-4 text-xs text-slate-500 whitespace-nowrap">
              {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}
              {r.updatedByEmail ? <div className="text-[10px] text-slate-400 truncate max-w-[160px]">{r.updatedByEmail}</div> : null}
            </td>
            <td className="p-4 text-right whitespace-nowrap">
              <button onClick={() => setEditing(r)} className="p-1.5 text-slate-400 hover:text-[#043132] rounded hover:bg-slate-100" title="Edit template"><Edit size={16} /></button>
            </td>
          </>
        )}
      />

      {editing ? (
        <EmailTemplateEditor
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}
